/**
 * Proposal Builder Prototype — localStorage helper.
 *
 * P1 SCOPE: this module is the SOLE persistence layer for the prototype.
 * No backend writes. No interaction with production collections.
 * Storage key is namespaced; can be wiped from browser DevTools at any time.
 */

const STORAGE_KEY = "reedos_proposal_builder_prototype_v1";

function uid(prefix) {
    // 8-char base36 — collision-free for prototype scale (<10k records)
    return `${prefix}-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
}

export const newPrototypeId = () => uid("PROTO");
export const newScopeId = () => uid("SCP");
export const newGroupId = () => uid("GRP");
export const newOfferId = () => uid("OFF");
export const newClauseId = () => uid("CLS");
export const newAmcId = () => uid("AMC");

export function defaultStorage() {
    return {
        currentPrototypeId: null,
        prototypes: {},
        reusableOffers: {},
    };
}

export function defaultPrototype(opts = {}) {
    const now = new Date().toISOString();
    const id = opts.id || newPrototypeId();
    return {
        id,
        title: opts.title || "New Proposal",
        clientName: opts.clientName || "",
        currency: opts.currency || "INR",
        scope: opts.scope || [],
        commercials: opts.commercials || {
            presentationMode: "itemized",   // itemized | grouped | consolidated
            itemPrices: {},                 // scopeId -> number (module or standalone product Setup Fee)
            annualCostPrices: {},           // scopeId -> number (Annual Cost; row.recurring_kind === "annual_cost")
            groups: [],                     // [{ id, label, productKeys:[], price (Setup Fee), amc }]
            consolidatedLabel: "Software Setup Fee",
            consolidatedAmount: 0,          // Setup Fee in Consolidated mode
            consolidatedAmc: 0,             // AMC in Consolidated mode
            deliveryModes: {},              // productKey -> ["browser", "android", ...]
            discount: {                     // applies to Setup Fee subtotal only
                type: "percent",            // percent | fixed
                value: 0,
                reason: "",
            },
        },
        amc: opts.amc || {
            items: [],                      // [{ id, scopeRowId, amount }] — per-scope-row AMC (iter 92.6: granular at module/line level)
        },
        taxDisplay: opts.taxDisplay || {
            mode: "show_gst",               // show_gst | taxes_extra
            gstPercent: 18,
        },
        tnc: opts.tnc || {
            mode: "full",                   // full | reference | reference_plus_additional
            fullTncId: null,
            referenceProposalNumber: "",
            referenceNote: "",
            additionalClauses: [],          // [{ id, title, body }]
        },
        createdAt: opts.createdAt || now,
        updatedAt: now,
    };
}

export function defaultReusableOffer(opts = {}) {
    const now = new Date().toISOString();
    return {
        id: opts.id || newOfferId(),
        name: opts.name || "Untitled Reusable Offer",
        description: opts.description || "",
        scope: opts.scope || [],
        commercials: opts.commercials || null,
        amc: opts.amc || null,
        taxDisplay: opts.taxDisplay || null,
        tnc: opts.tnc || null,
        createdAt: opts.createdAt || now,
        updatedAt: now,
    };
}

export function load() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaultStorage();
        const parsed = JSON.parse(raw);
        return { ...defaultStorage(), ...parsed };
    } catch {
        return defaultStorage();
    }
}

export function save(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (err) {
        // localStorage quota errors are not fatal for a prototype.
        console.warn("Prototype storage save failed:", err);
    }
}

export function listPrototypes() {
    const state = load();
    return Object.values(state.prototypes)
        .map(migratePrototypeShape)
        .sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
}

export function getPrototype(id) {
    const state = load();
    const p = state.prototypes[id] || null;
    return p ? migratePrototypeShape(p) : null;
}

export function upsertPrototype(prototype) {
    const state = load();
    const next = { ...prototype, updatedAt: new Date().toISOString() };
    state.prototypes[next.id] = next;
    state.currentPrototypeId = next.id;
    save(state);
    return next;
}

export function deletePrototype(id) {
    const state = load();
    delete state.prototypes[id];
    if (state.currentPrototypeId === id) state.currentPrototypeId = null;
    save(state);
}

export function setCurrentPrototypeId(id) {
    const state = load();
    state.currentPrototypeId = id;
    save(state);
}

export function getCurrentPrototypeId() {
    return load().currentPrototypeId;
}

export function listReusableOffers() {
    const state = load();
    return Object.values(state.reusableOffers).sort(
        (a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""),
    );
}

export function upsertReusableOffer(offer) {
    const state = load();
    const next = { ...offer, updatedAt: new Date().toISOString() };
    state.reusableOffers[next.id] = next;
    save(state);
    return next;
}

export function deleteReusableOffer(id) {
    const state = load();
    delete state.reusableOffers[id];
    save(state);
}

/* ─────────────────────────────────────────────────────────────────────
 * Clone helpers — five variants the operator can choose from.
 * Each returns a brand-new prototype object (NEW id, NEW timestamps).
 * Caller is responsible for upserting the result.
 * ───────────────────────────────────────────────────────────────────── */

export function cloneFull(source, titleSuffix = " (Copy)") {
    return defaultPrototype({
        title: (source.title || "Untitled") + titleSuffix,
        clientName: source.clientName,
        currency: source.currency,
        scope: JSON.parse(JSON.stringify(source.scope || [])),
        commercials: JSON.parse(JSON.stringify(source.commercials)),
        amc: JSON.parse(JSON.stringify(source.amc)),
        taxDisplay: JSON.parse(JSON.stringify(source.taxDisplay)),
        tnc: JSON.parse(JSON.stringify(source.tnc)),
    });
}

export function cloneScopeOnly(source) {
    return defaultPrototype({
        title: (source.title || "Untitled") + " (Scope Only Copy)",
        currency: source.currency,
        scope: JSON.parse(JSON.stringify(source.scope || [])),
    });
}

export function cloneCommercialsOnly(source) {
    return defaultPrototype({
        title: (source.title || "Untitled") + " (Commercials Only Copy)",
        currency: source.currency,
        commercials: JSON.parse(JSON.stringify(source.commercials)),
        amc: JSON.parse(JSON.stringify(source.amc)),
        taxDisplay: JSON.parse(JSON.stringify(source.taxDisplay)),
    });
}

export function cloneTncOnly(source) {
    return defaultPrototype({
        title: (source.title || "Untitled") + " (T&C Only Copy)",
        tnc: JSON.parse(JSON.stringify(source.tnc)),
    });
}

export function buildReusableOfferFromPrototype(source, name, description = "") {
    return defaultReusableOffer({
        name: name || source.title || "Untitled Reusable Offer",
        description,
        scope: JSON.parse(JSON.stringify(source.scope || [])),
        commercials: JSON.parse(JSON.stringify(source.commercials)),
        amc: JSON.parse(JSON.stringify(source.amc)),
        taxDisplay: JSON.parse(JSON.stringify(source.taxDisplay)),
        tnc: JSON.parse(JSON.stringify(source.tnc)),
    });
}

export function instantiatePrototypeFromOffer(offer, title) {
    return defaultPrototype({
        title: title || `${offer.name} — Instance`,
        scope: JSON.parse(JSON.stringify(offer.scope || [])),
        commercials: offer.commercials ? JSON.parse(JSON.stringify(offer.commercials)) : undefined,
        amc: offer.amc ? JSON.parse(JSON.stringify(offer.amc)) : undefined,
        taxDisplay: offer.taxDisplay ? JSON.parse(JSON.stringify(offer.taxDisplay)) : undefined,
        tnc: offer.tnc ? JSON.parse(JSON.stringify(offer.tnc)) : undefined,
    });
}

/* ─────────────────────────────────────────────────────────────────────
 * AMC start-rule reference table — REMOVED in iter 92.5 (2026-02-25).
 * Operator directive: the Proposal Builder no longer captures AMC
 * commencement (after Go-Live / Warranty / Acceptance / Custom). The
 * commercial proposal only carries the Setup Fee and Annual AMC amounts.
 * Commencement, if needed, is handled outside this builder.
 * ───────────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────────
 * Delivery / Platform modes — multi-selectable per Product. Influences
 * commercial scope and proposal wording (operator chips, not tied to
 * pricing math).
 * ───────────────────────────────────────────────────────────────────── */

/* Iter 97.2 (2026-02-26): "desktop" delivery mode retired per operator review —
   it is no longer a supported delivery channel and is removed from the master
   list entirely. The `iconName` field below drives the compact icon render in
   both the Commercial Structure and Scope sections (see deliveryIcons.jsx). */
export const DELIVERY_MODES = [
    { code: "browser", label: "Browser / Web", iconName: "globe" },
    { code: "android", label: "Android", iconName: "android" },
    { code: "ios", label: "iOS", iconName: "apple" },
    { code: "api", label: "API", iconName: "code" },
    { code: "data_feed", label: "Data Feed", iconName: "broadcast" },
];

export const DISCOUNT_TYPES = [
    { code: "percent", label: "Percentage (%)" },
    { code: "fixed", label: "Fixed Amount" },
];

export const ASSET_CLASS_SUGGESTIONS = [
    "Equity", "Mutual Fund", "Bond", "Insurance",
    "Cash", "Commodity", "Multi-Asset", "Alternative", "Derivative",
];

export const PRESENTATION_MODES = [
    { code: "itemized", label: "Itemized", description: "Show individual item prices + group/product subtotals + grand total." },
    { code: "grouped", label: "Grouped", description: "Hide individual item prices. Show group/product subtotals + grand total." },
    { code: "consolidated", label: "Consolidated", description: "Hide individual + subtotal prices. Show only the grand total." },
];

export const TAX_DISPLAY_MODES = [
    { code: "show_gst", label: "Show GST amount explicitly" },
    { code: "taxes_extra", label: "Mention \"Taxes extra as applicable\"" },
];

export const TNC_MODES = [
    { code: "full", label: "Full Terms & Conditions", description: "Include the complete T&C text from a master set." },
    { code: "reference", label: "Reference earlier proposal", description: "Reference a prior proposal/agreement number; its T&C continue to apply." },
    { code: "reference_plus_additional", label: "Reference + Additional clauses", description: "Reference prior + add specific incremental clauses." },
];


/* ─────────────────────────────────────────────────────────────────────
 * Schema migration (Iter 92.3) — best-effort upgrade of older prototypes
 * persisted in localStorage to the new Section 2/3 shape.
 *
 *   Commercials:
 *     - commercials.groups[*].scopeIds → commercials.groups[*].productKeys
 *     - commercials.deliveryModes default {}
 *     - commercials.discount default { type:"percent", value:0, reason:"" }
 *
 *   AMC:
 *     - amc.items[*] keyed by productKey (was scopeId; deduped by productKey)
 *     - startRule remap:
 *         "go_live"          → "after_go_live"
 *         "acceptance"       → "after_acceptance"
 *         "go_live_plus_1y"  → "custom" (note: "AMC commences one year after Go-Live.")
 *         "specific_date"    → "custom" (note: "AMC start date: <YYYY-MM-DD>")
 *         other unknown      → "after_go_live" (safe default)
 *     - specificDate field dropped (no calendar dates in proposal stage)
 *
 * Migration is idempotent — applied on every read; new data already in
 * the v2 shape is returned unchanged.
 * ───────────────────────────────────────────────────────────────────── */

function productKeyForRow(row) {
    if (!row) return null;
    if (row.source === "custom") return `custom:${row.id}`;
    if (row.productCode) return `master:${row.productCode}`;
    return null;
}

function migrateAmcItems(items, scope) {
    const seenScope = new Set();
    const out = [];
    // Build productKey → scope rows index from current scope.
    const rowsByProductKey = {};
    (scope || []).forEach((r) => {
        const k = productKeyForRow(r);
        if (!k) return;
        if (!rowsByProductKey[k]) rowsByProductKey[k] = [];
        rowsByProductKey[k].push(r);
    });

    (items || []).forEach((it) => {
        // Case 1 — already in the new shape (scopeRowId set).
        let scopeRowId = it.scopeRowId;

        // Case 2 — legacy iter 92.3–92.5 entry keyed by productKey. Map → first
        //         (and typically only) scope row if the product is standalone.
        //         Products with multiple modules drop the legacy AMC, since
        //         distributing one amount across N modules is ambiguous.
        if (!scopeRowId && it.productKey) {
            const rows = rowsByProductKey[it.productKey] || [];
            if (rows.length === 1) {
                scopeRowId = rows[0].id;
            } else {
                return; // can't safely migrate — drop
            }
        }

        // Case 3 — older iter 92.0–92.2 entry keyed by raw scopeId.
        if (!scopeRowId && it.scopeId) {
            const row = (scope || []).find((s) => s.id === it.scopeId);
            if (row) scopeRowId = row.id;
        }

        if (!scopeRowId || seenScope.has(scopeRowId)) return;
        seenScope.add(scopeRowId);

        out.push({
            id: it.id || newAmcId(),
            scopeRowId,
            amount: Number(it.amount) || 0,
        });
    });
    return out;
}

function migrateGroups(groups, scope) {
    return (groups || []).map((g) => {
        const productKeys = Array.isArray(g.productKeys)
            ? g.productKeys
            : (() => {
                const keys = [];
                (g.scopeIds || []).forEach((sid) => {
                    const row = (scope || []).find((s) => s.id === sid);
                    const key = productKeyForRow(row);
                    if (key && !keys.includes(key)) keys.push(key);
                });
                return keys;
            })();
        return {
            id: g.id,
            label: g.label,
            price: Number(g.price) || 0,            // Setup Fee
            amc: Number(g.amc) || 0,                // NEW (Iter 92.4) — AMC per group
            productKeys,
        };
    });
}

export function migratePrototypeShape(p) {
    if (!p) return p;
    const scope = p.scope || [];
    const com = p.commercials || {};
    const migratedCommercials = {
        presentationMode: com.presentationMode || "itemized",
        itemPrices: com.itemPrices || {},
        annualCostPrices: com.annualCostPrices || {},
        consolidatedLabel: com.consolidatedLabel || "Software Setup Fee",
        consolidatedAmount: Number(com.consolidatedAmount) || 0,
        consolidatedAmc: Number(com.consolidatedAmc) || 0,         // NEW (Iter 92.4)
        deliveryModes: com.deliveryModes && typeof com.deliveryModes === "object"
            ? Object.fromEntries(
                Object.entries(com.deliveryModes).map(([k, v]) => [
                    k,
                    Array.isArray(v) ? v.filter((c) => c !== "desktop") : [],
                ]),
            )
            : {},
        discount: com.discount && typeof com.discount === "object"
            ? {
                type: com.discount.type === "fixed" ? "fixed" : "percent",
                value: Number(com.discount.value) || 0,
                reason: com.discount.reason || "",
            }
            : { type: "percent", value: 0, reason: "" },
        groups: migrateGroups(com.groups, scope),
    };
    const migratedAmc = { items: migrateAmcItems(p.amc?.items, scope) };
    return { ...p, commercials: migratedCommercials, amc: migratedAmc };
}
